<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
<script setup>
/* eslint-disable prettier/prettier, vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
import { computed, ref, watch } from "vue";

const props = defineProps({
  preview: { type: Boolean, default: false },
  records: { type: Array, default: () => [] },
  agenda: { type: Array, default: () => [] },
  metrics: { type: Array, default: () => [] },
  history: { type: Array, default: () => [] },
  total: { type: Number, default: 0 },
});

const emit = defineEmits(["navigate"]);

const unavailable = "Não disponibilizado pela API";

const sourceOf = (record) => record?.source || record || {};
const pathValue = (value, path) =>
  path.split(".").reduce((current, key) => current?.[key], value);
const firstValue = (value, paths, fallback = "") => {
  const result = paths
    .map((path) => pathValue(value, path))
    .find((item) => item !== undefined && item !== null && item !== "");

  return result ?? fallback;
};
const initials = (name) =>
  String(name || "—")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
const asDate = (value) => {
  const parsed = new Date(value);
  return Number.isNaN(parsed.valueOf()) ? null : parsed;
};
const dateKey = (value) => {
  const parsed = asDate(value);
  if (!parsed) return "undated";

  return [parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()]
    .map((part) => String(part).padStart(2, "0"))
    .join("-");
};
const formatTime = (value) => {
  const parsed = asDate(value);
  return parsed
    ? new Intl.DateTimeFormat("pt-BR", {
        hour: "2-digit",
        minute: "2-digit",
      }).format(parsed)
    : unavailable;
};
const formatDay = (value) => {
  const parsed = asDate(value);
  return parsed
    ? new Intl.DateTimeFormat("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })
        .format(parsed)
        .replace(/^./, (character) => character.toUpperCase())
    : "Sem data agendada";
};
const compactDay = (value) => {
  const parsed = asDate(value);
  return parsed
    ? new Intl.DateTimeFormat("pt-BR", {
        weekday: "short",
        day: "2-digit",
        month: "short",
      })
        .format(parsed)
        .toUpperCase()
        .replace(".", "")
    : "SEM DATA";
};
const formatActivityDate = (value) => {
  const parsed = asDate(value);
  return parsed
    ? new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
      }).format(parsed)
    : unavailable;
};
const statusTone = (status) => {
  const normalizedStatus = String(status || "").toLowerCase();

  if (/cancel|recus|reprov/.test(normalizedStatus)) {
    return "bg-rose-50 text-rose-700";
  }
  if (/conclu|finaliz/.test(normalizedStatus)) {
    return "bg-emerald-50 text-emerald-700";
  }
  if (/confirm/.test(normalizedStatus)) {
    return "bg-violet-50 text-[#5a24c7]";
  }
  if (/agend|solicit|pend/.test(normalizedStatus)) {
    return "bg-amber-50 text-amber-700";
  }

  return "bg-slate-100 text-slate-600";
};

const agendaRecords = computed(() =>
  props.agenda.flatMap((agendaDay) => {
    if (Array.isArray(agendaDay?.visits)) return agendaDay.visits;
    if (Array.isArray(agendaDay?.records)) return agendaDay.records;
    if (Array.isArray(agendaDay?.items)) return agendaDay.items;
    if (agendaDay?.id || agendaDay?.visit_id) return [agendaDay];

    return [];
  }),
);
const visits = computed(() => {
  const records = agendaRecords.value.length
    ? agendaRecords.value
    : props.records;

  return records
    .map((record) => {
      const source = sourceOf(record);
      const scheduledAt = firstValue(source, [
        "scheduled_at",
        "scheduled_for",
        "starts_at",
        "start_at",
        "agenda_at",
        "date",
      ]);

      return {
        access: firstValue(source, [
          "access_instructions",
          "access_notes",
          "additional_attributes.entry_access.instructions",
          "additional_attributes.entry_access.helper",
          "property.access_instructions",
          "imovel.access_instructions",
        ]),
        address: firstValue(source, [
          "imovel.endereco",
          "imovel.address",
          "imovel_endereco",
          "property.address",
          "listing.address",
          "address.endereco",
        ]),
        broker: firstValue(source, [
          "broker.name",
          "responsible.name",
          "assignee.name",
          "owner.name",
          "user.name",
          "agent.name",
          "assigned_to.name",
          "assignedTo.name",
          "user_name",
        ]),
        client: firstValue(source, [
          "contact.name",
          "contact.full_name",
          "contact_name",
          "client.name",
          "visitor.name",
          "lead.name",
        ]),
        image: firstValue(source, [
          "imovel.image_url",
          "imovel.cover_url",
          "property.image_url",
          "property.cover_url",
          "listing.image_url",
          "listing.cover_url",
          "image_url",
          "image",
        ]),
        id: String(
          firstValue(record, ["id"], firstValue(source, ["id"])) || "",
        ),
        property:
          record?.title ||
          firstValue(source, [
            "imovel.titulo",
            "imovel.title",
            "imovel_titulo",
            "property.title",
            "listing.title",
            "address.title",
            "visit_code",
          ]),
        scheduledAt,
        status:
          firstValue(record, ["status", "status_label"]) ||
          firstValue(source, ["status_label", "status"]),
        visitType: firstValue(source, [
          "visit_type_label",
          "visit_type",
          "type",
        ]),
      };
    })
    .sort(
      (leftVisit, rightVisit) =>
        (asDate(leftVisit.scheduledAt)?.valueOf() || Number.MAX_SAFE_INTEGER) -
        (asDate(rightVisit.scheduledAt)?.valueOf() || Number.MAX_SAFE_INTEGER),
    );
});
const groups = computed(() => {
  const groupsByDay = new Map();

  visits.value.forEach((visit) => {
    const key = dateKey(visit.scheduledAt);
    if (!groupsByDay.has(key)) {
      groupsByDay.set(key, {
        date: visit.scheduledAt,
        key,
        label: formatDay(visit.scheduledAt),
        visits: [],
      });
    }
    groupsByDay.get(key).visits.push(visit);
  });

  return [...groupsByDay.values()].sort(
    (leftGroup, rightGroup) =>
      (asDate(leftGroup.date)?.valueOf() || Number.MAX_SAFE_INTEGER) -
      (asDate(rightGroup.date)?.valueOf() || Number.MAX_SAFE_INTEGER),
  );
});
const calendarDays = computed(() =>
  groups.value.filter((group) => group.key !== "undated").slice(0, 5),
);
const statusFilters = computed(() =>
  [...new Set(visits.value.map((visit) => visit.status).filter(Boolean))].slice(
    0,
    3,
  ),
);
const activity = computed(() =>
  props.history.slice(0, 4).map((event, index) => ({
    actor:
      typeof event?.actor === "object"
        ? firstValue(
            event.actor,
            ["name", "full_name", "display_name"],
            unavailable,
          )
        : event?.actor || unavailable,
    date: formatActivityDate(event?.date),
    id: String(event?.id || `${event?.label || "activity"}-${index}`),
    label: event?.label || unavailable,
  })),
);
const emptyAgendaMessage = computed(() =>
  props.preview
    ? "A prévia não usa agenda ou visitas simuladas."
    : "A agenda será preenchida quando a API retornar visitas.",
);

const selectedId = ref("");
const selectedDayKey = ref("");
const selectedStatus = ref("");
const searchQuery = ref("");

watch(
  visits,
  (currentVisits) => {
    if (!currentVisits.some((visit) => visit.id === selectedId.value)) {
      selectedId.value = currentVisits.find((visit) => visit.id)?.id || "";
    }
  },
  { immediate: true },
);
watch(
  calendarDays,
  (currentDays) => {
    if (!currentDays.some((day) => day.key === selectedDayKey.value)) {
      selectedDayKey.value = currentDays[0]?.key || "";
    }
  },
  { immediate: true },
);
watch(
  statusFilters,
  (currentStatuses) => {
    if (!currentStatuses.includes(selectedStatus.value)) {
      selectedStatus.value = "";
    }
  },
  { immediate: true },
);

const visibleGroups = computed(() =>
  groups.value
    .filter(
      (group) => !selectedDayKey.value || group.key === selectedDayKey.value,
    )
    .map((group) => ({
      ...group,
      visits: group.visits.filter(
        (visit) =>
          (!selectedStatus.value || visit.status === selectedStatus.value) &&
          [visit.client, visit.property, visit.broker, visit.status]
            .filter(Boolean)
            .some((value) =>
              String(value)
                .toLocaleLowerCase("pt-BR")
                .includes(searchQuery.value.trim().toLocaleLowerCase("pt-BR")),
            ),
      ),
    }))
    .filter((group) => group.visits.length),
);
const selected = computed(
  () => visits.value.find((visit) => visit.id === selectedId.value) || null,
);

const selectVisit = (visit) => {
  if (visit.id) selectedId.value = visit.id;
};
const open = (screen) => {
  if (!selected.value?.id) return;

  emit("navigate", { id: selected.value.id, screen });
};
</script>

<template>
  <main class="min-h-full w-full bg-[#faf9fd] px-4 py-5 text-[#17203d]">
    <section class="grid min-w-0 gap-4 xl:grid-cols-[236px_493px_minmax(0,1fr)]">
      <aside data-imudei-visual-id="sidebar" class="space-y-3">
        <article class="h-[128px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center gap-2">
            <i class="i-lucide-target size-4 text-[#f59d1f]" />
            <h2 class="text-[10px] font-bold">Missão do dia</h2>
          </header>
          <p class="mt-4 text-[9px] leading-4 text-[#69769b]">Missões e progresso não são disponibilizados pelo backend atual.</p>
          <div class="mt-4 h-1.5 rounded-full bg-[#eeeaf7]"><span class="block h-full w-0 rounded-full bg-[#5a24c7]" /></div>
          <small class="mt-2 block text-[8px] text-[#7883a2]">—</small>
        </article>

        <article class="h-[247px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Ranking da semana</h2>
            <i class="i-lucide-medal size-4 text-[#5a24c7]" />
          </header>
          <div class="mt-5 flex items-start gap-3">
            <i class="grid size-9 shrink-0 place-items-center rounded-full bg-violet-50 text-[#5a24c7]"><i class="i-lucide-users-round size-4" /></i>
            <span>
              <strong class="block text-[9px]">Não disponibilizado</strong>
              <small class="mt-1 block text-[8px] leading-4 text-[#7883a2]">A API não retornou classificação social para esta agenda.</small>
            </span>
          </div>
        </article>

        <article class="h-[162px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Conquistas</h2>
            <i class="i-lucide-badge-help size-3.5 text-[#a58add]" />
          </header>
          <div class="mt-5 grid grid-cols-3 gap-2 text-center">
            <span v-for="index in 3" :key="index">
              <i class="mx-auto grid size-10 place-items-center rounded-full bg-[#f5f2fa] text-[#a58add]"><i class="i-lucide-lock-keyhole size-3.5" /></i>
              <small class="mt-2 block text-[7px] text-[#7883a2]">Indisponível</small>
            </span>
          </div>
        </article>

        <article class="h-[291px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
          <header class="flex items-center justify-between">
            <h2 class="text-[10px] font-bold">Feed de atividades</h2>
            <i class="i-lucide-history size-3.5 text-[#5a24c7]" />
          </header>
          <div v-for="item in activity" :key="item.id" class="mt-4 flex gap-2">
            <i class="grid size-7 shrink-0 place-items-center rounded-full bg-violet-100 text-[#5a24c7]"><i class="i-lucide-history size-3" /></i>
            <span class="min-w-0"><b class="block truncate text-[8px]">{{ item.label }}</b><small class="mt-1 block truncate text-[7px] text-[#7883a2]">{{ item.actor }} · {{ item.date }}</small></span>
          </div>
          <p v-if="!activity.length" class="mt-4 text-[8px] leading-4 text-[#7883a2]">Nenhuma atividade real foi disponibilizada para este contexto.</p>
        </article>
      </aside>

      <section class="min-w-0">
        <header data-imudei-visual-id="visit-dashboard-header" class="flex h-[64px] items-start justify-between gap-3 pt-3">
          <span>
            <h1 class="text-[22px] font-bold tracking-[-0.035em]">Visitas</h1>
            <p class="mt-1 text-[9px] text-[#66739b]">Gerencie e acompanhe suas visitas.</p>
          </span>
          <div class="flex shrink-0 items-center gap-2">
            <label class="flex h-9 w-[176px] items-center gap-2 rounded-xl border border-[#e7e2ef] bg-white px-3 text-[8px] text-[#7883a2]">
              <input v-model="searchQuery" class="min-w-0 flex-1 bg-transparent text-[8px] text-[#17203d] outline-none placeholder:text-[#7883a2]" placeholder="Buscar visitas..." type="search" aria-label="Buscar visitas" />
              <i class="i-lucide-search size-3.5" />
            </label>
            <button class="grid size-9 place-items-center rounded-xl border border-[#e7e2ef] bg-white text-[#5a24c7]" type="button" aria-label="Limpar filtros" @click="selectedDayKey = ''; selectedStatus = ''; searchQuery = ''"><i class="i-lucide-list-filter size-4" /></button>
          </div>
        </header>

        <nav data-imudei-visual-id="calendar" class="mt-3 grid grid-cols-5 gap-2">
          <button v-for="day in calendarDays" :key="day.key" class="min-h-[86px] rounded-xl border bg-white px-2 py-3 text-center transition" :class="selectedDayKey === day.key ? 'border-[#5a24c7] bg-violet-50 text-[#5a24c7]' : 'border-[#ece8f4] text-[#66739b]'" type="button" @click="selectedDayKey = selectedDayKey === day.key ? '' : day.key">
            <small class="block text-[7px] font-bold">{{ compactDay(day.date) }}</small>
            <strong class="mx-auto mt-3 grid size-7 place-items-center rounded-full bg-violet-50 text-[9px] text-[#5a24c7]">{{ day.visits.length }}</strong>
          </button>
          <span v-for="slot in Math.max(0, 5 - calendarDays.length)" :key="`empty-calendar-${slot}`" class="min-h-[86px] rounded-xl border border-[#f0edf4] bg-white" />
        </nav>
        <p v-if="!calendarDays.length" class="mt-3 text-[8px] text-[#7883a2]">Datas de agenda não foram disponibilizadas pela API.</p>

        <section data-imudei-visual-id="schedule" class="mt-5">
          <span class="sr-only">{{ metrics.length ? `${metrics.length} indicadores foram retornados pela API.` : 'Nenhum indicador foi retornado pela API.' }}</span>
          <div v-if="statusFilters.length" class="sr-only" aria-live="polite">Filtro ativo: {{ selectedStatus || 'Todos' }}</div>
          <div v-if="visibleGroups.length" class="space-y-3">
            <article v-for="group in visibleGroups" :key="group.key" class="overflow-hidden rounded-2xl border border-[#e8e4ef] bg-white">
              <header class="flex items-center justify-between border-b border-[#eeeaf4] px-4 py-3">
                <span>
                  <h2 class="text-[13px] font-bold">{{ group.label }}</h2>
                  <small class="mt-1 block text-[7px] text-[#7883a2]">{{ group.visits.length }} visita(s) retornada(s)</small>
                </span>
                <small class="text-[8px] text-[#66739b]">{{ group.visits.length }} visita(s)</small>
              </header>
              <button v-for="(visit, visitIndex) in group.visits" :key="visit.id || `${group.key}-${visitIndex}`" class="grid min-h-[82px] w-full grid-cols-[48px_minmax(0,1fr)_auto] items-center gap-3 border-b border-[#f0edf4] px-4 py-3 text-left last:border-b-0 transition" :class="selectedId === visit.id ? 'bg-violet-50/45' : 'hover:bg-[#fcfbfe]'" :disabled="!visit.id" type="button" @click="selectVisit(visit)">
                <span class="border-r border-[#eeeaf4] pr-3 text-center"><strong class="block text-[11px] text-[#4b19cf]">{{ formatTime(visit.scheduledAt) }}</strong><small class="mt-1 block text-[7px] text-[#7883a2]">Horário</small></span>
                <span class="flex min-w-0 items-center gap-3">
                  <i class="grid size-11 shrink-0 place-items-center rounded-full bg-gradient-to-br from-[#6530d8] via-[#ef4eba] to-[#ffb13c] p-0.5"><i class="grid size-9 place-items-center rounded-full bg-white text-[8px] font-bold text-[#5a24c7]">{{ initials(visit.client) }}</i></i>
                  <span class="min-w-0"><b class="block truncate text-[10px]">{{ visit.client || unavailable }}</b><small class="mt-1 block truncate text-[8px] text-[#69769b]">{{ visit.property || unavailable }}</small></span>
                </span>
                <span class="flex items-center gap-2"><small class="max-w-[88px] truncate rounded-lg px-2 py-1.5 text-[7px] font-bold" :class="statusTone(visit.status)">{{ visit.status || unavailable }}</small><i :class="visit.id ? 'i-lucide-ellipsis' : 'i-lucide-circle-alert'" class="size-4 text-[#5d6687]" /></span>
              </button>
            </article>
          </div>
          <div v-else class="grid min-h-[500px] place-items-center rounded-2xl border border-dashed border-[#e8e4ef] bg-white text-center">
            <span class="max-w-[240px] px-5"><i class="i-lucide-calendar-x mx-auto size-8 text-[#a58add]" /><strong class="mt-3 block text-[11px]">Nenhuma visita disponível</strong><small class="mt-1 block text-[8px] leading-4 text-[#7883a2]">{{ emptyAgendaMessage }}</small></span>
          </div>
          <p class="mt-3 text-[8px] text-[#7883a2]">{{ visits.length }} de {{ total || visits.length }} visitas reais</p>
        </section>
      </section>

      <aside data-imudei-visual-id="detail" class="min-h-[900px] rounded-2xl border border-[#e8e4ef] bg-white p-4">
        <template v-if="selected">
          <div class="flex min-h-[868px] flex-col">
            <header class="flex items-center justify-between gap-3">
              <h2 class="text-[12px] font-bold">Visualização rápida da visita</h2>
              <button class="i-lucide-x size-4 text-[#5d6687]" type="button" aria-label="Fechar visualização" @click="selectedId = ''" />
            </header>
            <div class="mt-3 flex items-center gap-2">
              <i class="grid size-8 place-items-center rounded-full bg-violet-100 text-[8px] font-bold text-[#5a24c7]">{{ initials(selected.client) }}</i>
              <i class="grid size-8 place-items-center rounded-full bg-sky-100 text-[8px] font-bold text-sky-700">{{ initials(selected.broker) }}</i>
              <small class="ml-1 max-w-[140px] truncate rounded-lg px-2 py-1 text-[7px] font-bold" :class="statusTone(selected.status)">{{ selected.status || unavailable }}</small>
            </div>

            <section class="mt-4 grid h-[320px] overflow-hidden rounded-xl border border-[#eeeaf4] grid-cols-[minmax(0,0.94fr)_minmax(0,1.06fr)]">
              <div class="relative min-w-0 overflow-hidden bg-[#f3ecdf]">
                <img v-if="selected.image" :src="selected.image" :alt="selected.property || 'Imagem do imóvel'" class="h-full w-full object-cover" />
                <span v-else class="grid h-full place-items-center bg-gradient-to-br from-[#eee4d2] via-[#faf7ef] to-[#d9e7d9] px-5 text-center text-[#79623e]"><i class="i-lucide-house size-9" /><small class="mt-3 block text-[7px] leading-3">Imagem do imóvel não disponibilizada pela API</small></span>
                <span class="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3"><small class="block text-[8px] text-[#69769b]">Agendada para</small><strong class="mt-1 block text-[19px] leading-none">{{ formatTime(selected.scheduledAt) }}</strong><small class="mt-2 block text-[8px] text-[#69769b]">{{ formatDay(selected.scheduledAt) }}</small></span>
              </div>
              <div class="grid min-w-0 grid-rows-[minmax(0,1fr)_108px]">
                <section class="min-w-0 p-4">
                  <small class="rounded bg-violet-50 px-2 py-1 text-[8px] text-[#5a24c7]">{{ selected.visitType || unavailable }}</small>
                  <h3 class="mt-3 break-words text-[12px] font-bold leading-5">{{ selected.property || unavailable }}</h3>
                  <p class="mt-2 break-words text-[8px] leading-4 text-[#69769b]">{{ selected.address || unavailable }}</p>
                </section>
                <div class="relative grid place-items-center border-t border-[#eeeaf4] bg-[#f5f2ed] text-[#806839]"><i class="i-lucide-map-pinned size-8" /><small class="absolute bottom-2 left-2 rounded bg-white/75 px-2 py-1 text-[7px] font-bold">Mapa não disponibilizado pela API</small></div>
              </div>
            </section>

            <section class="mt-6 grid grid-cols-[0.94fr_minmax(0,1.06fr)] gap-4">
              <div>
                <h3 class="text-[10px] font-bold">Status da visita</h3>
                <span class="mt-3 inline-block rounded-lg px-3 py-1.5 text-[8px] font-bold" :class="statusTone(selected.status)">{{ selected.status || unavailable }}</span>
                <p class="mt-3 text-[8px] leading-4 text-[#69769b]">As etapas abaixo só são marcadas quando retornadas pela API.</p>
                <h3 class="mt-5 text-[10px] font-bold">Checklist</h3>
                <div class="mt-3 space-y-3 text-[8px]">
                  <span class="flex items-center gap-2"><i class="i-lucide-circle-check size-3 text-[#5a24c7]" />Status atual: {{ selected.status || unavailable }}</span>
                  <span class="flex items-center gap-2 text-[#7883a2]"><i class="i-lucide-circle size-3" />Confirmação não retornada</span>
                  <span class="flex items-center gap-2 text-[#7883a2]"><i class="i-lucide-circle size-3" />Conclusão não retornada</span>
                  <span class="flex items-center gap-2 text-[#7883a2]"><i class="i-lucide-circle size-3" />Resultado não retornado</span>
                </div>
              </div>
              <div class="border-l border-[#eeeaf4] pl-4 text-[8px]">
                <section class="border-b border-[#eeeaf4] pb-4"><small class="block text-[#69769b]">Cliente</small><b class="mt-2 block break-words text-[9px]">{{ selected.client || unavailable }}</b></section>
                <section class="border-b border-[#eeeaf4] py-4"><small class="block text-[#69769b]">Corretor responsável</small><b class="mt-2 block break-words text-[9px]">{{ selected.broker || unavailable }}</b></section>
                <section class="border-b border-[#eeeaf4] py-4"><small class="block text-[#69769b]">Tipo de visita</small><b class="mt-2 block break-words text-[9px]">{{ selected.visitType || unavailable }}</b></section>
                <section class="pt-4"><small class="block text-[#69769b]">Instruções de acesso</small><p class="mt-2 break-words leading-4 text-[#69769b]">{{ selected.access || 'Não disponibilizadas pela API.' }}</p></section>
              </div>
            </section>

            <footer data-imudei-visual-id="footer" class="mt-auto flex gap-3 border-t border-[#eeeaf4] pt-4">
              <button class="h-10 flex-1 rounded-lg border border-[#d7c8ff] text-[8px] font-bold text-[#5a24c7]" type="button" @click="open('edit')">Editar</button>
              <button class="h-10 flex-[1.2] rounded-lg bg-gradient-to-r from-[#5620ce] to-[#7828ec] text-[8px] font-bold text-white" type="button" @click="open('detail')">Gerenciar visita</button>
            </footer>
          </div>
        </template>
        <div v-else class="grid min-h-[868px] place-items-center text-center">
          <span class="max-w-[180px]"><i class="i-lucide-panel-right-open mx-auto size-8 text-[#a58add]" /><strong class="mt-3 block text-[10px]">Selecione uma visita</strong><small class="mt-1 block text-[8px] leading-4 text-[#7180a4]">A visualização detalhada requer um identificador retornado pela API.</small></span>
        </div>
      </aside>
    </section>
  </main>
</template>
